import classNames from 'classnames';
import { FC } from 'react';

import {
  Exact,
  MeProviderProgramTagsQuery,
  PatientTableDataFragment,
  ProgramTagDataFragment,
} from '../../../../../generated/graphql';

import { getRelativeTimestamp, getShortDate } from '../../../../lib/time';
import { AnalyticsPage } from '../../../../../lib/analytics';
import Avatar from '../../../../components/Avatar';
import NoText from '../../../../components/NoText';
import TestClientPill from '../../../../components/TestClientPill';
import ProgramTagManager from '../../../../components/ProgramTag/ProgramTagManager';
import { ApolloQueryResult } from '@apollo/client';
import Bolt from '../../../../svgs/Bolt';
import { isPatientNewFromStorefront } from '../../../../lib/patient';
import { useGlobalModalsContext } from '../../../../../contexts/GlobalModalsContext';
import { UserAddIcon } from '@heroicons/react/outline';
import { useAuth } from '../../../../../contexts/AuthContext';
import StackedAvatars from '../../../ClientSessions/components/StackedAvatars';
import { useNavigate } from 'react-router-dom';
import { useColumnVisibility } from './useColumnVisibility';

const RestrictedRow: FC<{
  patient: PatientTableDataFragment;
  showCheckboxColumn?: boolean;
  providerProgramTags?: ProgramTagDataFragment[];
  refetchProviderProgramTags?: (
    variables?:
      | Partial<
          Exact<{
            [key: string]: never;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<MeProviderProgramTagsQuery>>;
  handleProgramTagsUpdate: (
    programId: string,
    tags: ProgramTagDataFragment[],
  ) => void;
  handleAddStorefrontClient: (programId: string) => Promise<void>;
  visibleColumns: string[];
}> = ({
  patient,
  showCheckboxColumn = false,
  providerProgramTags = [],
  refetchProviderProgramTags,
  handleProgramTagsUpdate,
  handleAddStorefrontClient,
  visibleColumns,
}) => {
  const { willHitClientLimit } = useAuth();
  const { showUpgradeModal } = useGlobalModalsContext();
  const navigate = useNavigate();
  const hasUnreadComments = patient?.aggregateUnreadCommentCount > 0;
  const isNewStorefrontPatient = isPatientNewFromStorefront(patient);

  const { shouldShowColumn } = useColumnVisibility(visibleColumns);

  return (
    <tr
      className={classNames(
        'group max-w-full cursor-pointer whitespace-nowrap text-green-150 hover:bg-neutral-25',
        isNewStorefrontPatient && 'bg-green-25 hover:bg-green-50/30',
      )}
      onClick={(event) => {
        event.stopPropagation();
        showUpgradeModal(AnalyticsPage.ClientsTableActive);
      }}
    >
      <td className="w-[3.75rem]">
        <div className="flex justify-center">
          {isNewStorefrontPatient && (
            <Bolt className="h-[1.125rem] w-[1.125rem] text-orange-100" />
          )}
        </div>
      </td>

      {showCheckboxColumn && <td className="w-[3.75rem]" />}

      {shouldShowColumn('name') && (
        <td className="py-4 px-0">
          <div className="flex flex-row items-center gap-x-4">
            <Avatar
              imageUrl={patient?.profileImageMedia?.url}
              name={patient?.firstName ?? ''}
              size="medium"
            />
            <div className="font-serif text-extra-small">
              {patient?.firstName + ' ' + patient?.lastName}
            </div>
            {patient?.isTestProgram && <TestClientPill />}
          </div>
        </td>
      )}

      {shouldShowColumn('careTeam') && (
        <td className="py-4 px-0">
          <StackedAvatars
            items={
              patient?.assignedProviderUsers?.map((user) => ({
                name: user.name || user.email,
                description: user.email,
                imageUrl: user.profileImageMedia?.url,
                url: `/provider-profile/${user.id}`,
              })) ?? []
            }
            onAvatarClick={(item) => navigate(item.url!)}
          />
        </td>
      )}

      {shouldShowColumn('tags') && (
        <td className="max-w-[300px]">
          <ProgramTagManager
            key={JSON.stringify(patient.tags)}
            programId={patient.programInstanceId}
            availableProgramTags={providerProgramTags}
            refreshAvailableProgramTags={refetchProviderProgramTags}
            selectedProgramTags={patient.tags}
            onTagsUpdated={(tags) => {
              handleProgramTagsUpdate(patient.programInstanceId, tags);
            }}
            analyticsPage={AnalyticsPage.ClientsTableActive}
            className="w-full max-w-full"
          />
        </td>
      )}

      {shouldShowColumn('programStartDate') && (
        <td className="py-4 px-0">
          {patient.programStartDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programStartDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('programEndDate') && (
        <td className="py-4 px-0">
          {patient.programEndDate ? (
            <span className="text-small-caption">
              {getShortDate(patient.programEndDate)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('dateAdded') && (
        <td className="py-4 px-0">
          {patient.createdAt ? (
            <span className="text-small-caption">
              {getShortDate(patient.createdAt)}
            </span>
          ) : (
            <NoText text="—" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('unreadComments') && (
        <td>
          {hasUnreadComments ? (
            <div className="flex flex-row items-center gap-x-3 text-caption">
              <div className="flex h-7 w-7 flex-row items-center justify-center rounded-full bg-secondary-100 text-small-caption font-medium text-neutral-0">
                {patient.aggregateUnreadCommentCount}
              </div>
              <span className="text-neutral-110">
                {getRelativeTimestamp(patient.latestUnreadCommentAt)}
              </span>
            </div>
          ) : (
            <NoText text="None" className="text-caption" />
          )}
        </td>
      )}

      {shouldShowColumn('sendFollowUp') && (
        <td>
          {!willHitClientLimit() && (
            <button
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleAddStorefrontClient(patient.programInstanceId);
              }}
              className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
            >
              <UserAddIcon className="h-5 w-5 text-neutral-125" />
            </button>
          )}
        </td>
      )}
    </tr>
  );
};

export default RestrictedRow;
